import React from "react";
import { useTranslation } from "react-i18next";

function ProcessSection() {
  const { t } = useTranslation();

  const steps = [
    {
      icon: "fas fa-walking",
      text: t("taolu_process.step1"),
    },
    {
      icon: "fas fa-clock",
      text: t("taolu_process.step2"),
    },
    {
      icon: "fas fa-gavel",
      text: t("taolu_process.step3"),
    },
    {
      icon: "fas fa-trophy",
      text: t("taolu_process.step4"),
    },
  ];

  return (
    <section className="pt-12 pb-12 bg-white dark:bg-slate-800 transition-colors" id="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
            {t("taolu_process.title")}
            {/* Underline decoration */}
            <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
          </h2>
        </div>

        {/* Two Column Layout */}
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Process Timeline */}
            <div className="order-2 lg:order-1 flex flex-col">
              <div className="flex-1">
                {/* Decorative Line Above */}
                <div className="flex items-center justify-center mb-8">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-slate-300 dark:from-transparent dark:via-slate-600 dark:to-slate-600"></div>
                  <div className="mx-4 flex items-center justify-center">
                    <i className="fas fa-play-circle text-primary-light dark:text-primary-light text-2xl opacity-70"></i>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-300 to-slate-300 dark:from-transparent dark:via-slate-600 dark:to-slate-600"></div>
                </div>

                {/* Timeline Steps */}
                <div className="relative">
                  {/* Vertical Timeline Line - Connecting between boxes */}
                  <div className="absolute left-8 top-16 bottom-16 w-0.5 bg-gradient-to-b from-primary/30 via-primary/20 to-primary/30 dark:from-primary-light/30 dark:via-primary-light/20 dark:to-primary-light/30 -z-10"></div>

                  <div className="space-y-6">
                    {steps.map((step, index) => (
                      <div
                        key={index}
                        className="relative ml-8"
                      >
                        {/* Timeline Node - Half embedded in box, vertical center on box edge */}
                        <div className="absolute -left-8 top-1/2 transform -translate-y-1/2 w-16 h-16 flex items-center justify-center z-30">
                          <div className="w-16 h-16 rounded-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
                            <i className={`${step.icon} text-primary dark:text-primary-light text-xl`}></i>
                          </div>
                          {/* Step Number Badge */}
                          <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-primary dark:bg-primary-light flex items-center justify-center shadow-md z-40">
                            <span className="text-white text-xs font-bold">{index + 1}</span>
                          </div>
                        </div>

                        {/* Content Card - Fixed height, centered text */}
                        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200 h-32 flex items-center justify-center p-6 pl-8 relative z-10">
                          <p className="text-slate-700 dark:text-slate-300 text-base leading-relaxed text-center">
                            {step.text}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Decorative Line Below */}
                <div className="flex items-center justify-center mt-8 mb-6">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-slate-300 dark:from-transparent dark:via-slate-600 dark:to-slate-600"></div>
                  <div className="mx-4 flex items-center justify-center">
                    <i className="fa-brands fa-pagelines text-slate-400 dark:text-slate-500 text-lg"></i>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-300 to-slate-300 dark:from-transparent dark:via-slate-600 dark:to-slate-600"></div>
                </div>
              </div>

              {/* Conclusion Card */}
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary-light/10 dark:to-primary-light/5 rounded-lg border border-primary/20 dark:border-primary-light/20 shadow-sm p-6">
                <p className="text-slate-700 dark:text-slate-300 text-base leading-relaxed text-center font-medium">
                  {t("taolu_process.conclusion")}
                </p>
              </div>
            </div>

            {/* Right Column - Image Frame */}
            <div className="order-1 lg:order-2 flex flex-col">
              <div className="w-full flex-1">
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200 h-full overflow-hidden">
                  {/* Image */}
                  <img 
                    src="/images/athlete_at_competition.png" 
                    alt="Athlete at competition" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProcessSection;

